<template>
  <!-- In-person vs virtual selector. `.attend-toggle-inline` / `.attend-option`
       styles come from the prototype stylesheet. v-model carries the option value. -->
  <div class="attend-toggle-inline">
    <div
      v-for="opt in options"
      :key="opt.value"
      class="attend-option"
      :class="{ selected: value === opt.value }"
      role="radio"
      :aria-checked="value === opt.value"
      tabindex="0"
      @click="select(opt.value)"
      @keydown.enter.prevent="select(opt.value)"
      @keydown.space.prevent="select(opt.value)"
    >
      <div class="ic" v-html="opt.icon"></div>
      <div>
        <div class="label">{{ opt.label }}</div>
        <div class="desc">{{ opt.desc }}</div>
      </div>
    </div>
  </div>
</template>

<script>
const PERSON_ICON = '<svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="7" r="4"/><path d="M5.5 21a6.5 6.5 0 0 1 13 0"/></svg>'
const GLOBE_ICON = '<svg aria-hidden="true" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>'

export default {
  name: 'lp-attend-toggle-inline',
  props: {
    value: { type: String, default: 'in-person' },
    options: {
      type: Array,
      default: () => ([
        { value: 'in-person', label: 'In-person', desc: 'Attend on-site in Toronto', icon: PERSON_ICON },
        { value: 'virtual', label: 'Virtual', desc: 'Attend online (plenaries only)', icon: GLOBE_ICON },
      ]),
    },
  },
  methods: {
    select(v) {
      if (v !== this.value) this.$emit('input', v)
    },
  },
}
</script>

<style lang="scss">
.attend-toggle-inline {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-top: 14px;
  max-width: 560px;
}
.attend-toggle-inline .attend-option {
  padding: 10px 14px;
}
.attend-toggle-inline .attend-option .ic {
  width: 32px; height: 32px;
}
.attend-toggle-inline .attend-option .label {
  font-size: var(--fs-small);
}
.attend-toggle-inline .attend-option .desc {
  font-size: var(--fs-caption);
}
.attend-option {
  display: flex; align-items: center; gap: 12px;
  padding: 14px 16px;
  border: 1.5px solid var(--border);
  border-radius: var(--radius-md);
  cursor: pointer;
  background: var(--surface);
  transition: all 0.15s;
}
.attend-option:hover { border-color: var(--brand-light); background: var(--surface-alt); }
.attend-option.selected {
  border-color: var(--brand);
  background: var(--brand-soft);
}
.attend-option .ic {
  width: 40px; height: 40px; border-radius: 50%;
  background: var(--surface-alt);
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0;
  color: var(--brand-deep);
}
.attend-option.selected .ic { background: var(--brand); color: white; }
.attend-option .label { font-size: var(--fs-body); font-weight: 500; }
.attend-option .desc { font-size: var(--fs-label); color: var(--text-secondary); margin-top: 2px; }

/* Phones: the same segmented control as the category picker
   (lp-category-select), with the description as its small second line. */
@media (max-width: 640px) {
  .attend-toggle-inline {
    grid-template-columns: none;
    grid-auto-flow: column;
    grid-auto-columns: minmax(0, 1fr);
    gap: 4px;
    padding: 4px;
    max-width: none;
    background: var(--surface-alt);
    border-radius: 12px;
  }
  .attend-toggle-inline .attend-option {
    min-height: 52px;
    padding: 6px 4px;
    border: 0;
    border-radius: 9px;
    background: transparent;
    color: var(--text-secondary);
    justify-content: center;
    text-align: center;
    transition: background 0.15s, box-shadow 0.15s;
  }
  .attend-toggle-inline .attend-option:hover { background: transparent; }
  .attend-toggle-inline .attend-option.selected {
    background: var(--surface);
    color: var(--text);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.18);
  }
  .attend-toggle-inline .attend-option .ic { display: none; }
  .attend-toggle-inline .attend-option .label {
    font-size: var(--fs-small);
    font-weight: 600;
    line-height: 1.25;
  }
  .attend-toggle-inline .attend-option .desc {
    margin-top: 2px;
    font-size: 12px;
    color: var(--text-tertiary);
  }
  .attend-toggle-inline .attend-option:focus-visible { outline: 2px solid var(--brand); outline-offset: 2px; }
}
</style>
